/**
 * cart-minimum-guard.js — Layer 1 of the $120 minimum enforcement.
 *
 * Context (order #1243, 2026-09-13): the configurator bumps a custom line to
 * ceil(120 / $per_sqft) units at add-to-cart so the line bills at least $120.
 * That bump used to be undoable from the cart's quantity stepper, and #1243
 * checked out at $60 because 16 units were edited down to 8. The stepper is
 * gone as of 2026-09-14, so no customer reaches that state by clicking.
 *
 * This script repairs a cart that is ALREADY under the minimum -- which now
 * only happens via a scripted /cart/change.js call -- by silently raising the
 * quantity back to the billable minimum. No error, no message, no block.
 *
 * IMPORTANT: this is a convenience layer, not enforcement. It runs in the
 * browser, so anything that bypassed the UI to get here can bypass this too.
 * The Cart and Checkout Validation Function (Layer 2) is the only real
 * enforcement. This exists so legitimate customers never see Layer 2's error.
 *
 * Exemptions mirror Layer 2 exactly -- keep the two in step:
 *   - catalog swatch samples ($5)    -> variant title ends with "sample"
 *   - Custom Design Sample Kit ($65) -> product handle
 */
(function () {
  "use strict";

  var MINIMUM_CENTS = 12000; // $120.00
  var EXEMPT_HANDLES = ["custom-design-sample-kit"];
  var running = false;

  function isExempt(item) {
    var variantTitle = String(item.variant_title || "").trim().toLowerCase();
    if (variantTitle.slice(-6) === "sample") return true;
    if (EXEMPT_HANDLES.indexOf(item.handle) !== -1) return true;
    return false;
  }

  /**
   * Billable units needed to reach $120 on this line, or null if the line is
   * exempt / already compliant / has no usable unit price.
   *
   * Uses original_price (pre-discount) deliberately: a discount code is Ross's
   * choice and must not push a customer into buying more square footage.
   */
  function shortfallQuantity(item) {
    if (isExempt(item)) return null;
    var unit = item.original_price || item.price;
    if (!unit || unit <= 0) return null;
    if (item.original_line_price >= MINIMUM_CENTS) return null;
    var needed = Math.ceil(MINIMUM_CENTS / unit);
    return needed > item.quantity ? needed : null;
  }

  function getCart() {
    return fetch("/cart.js", { credentials: "same-origin" }).then(function (r) {
      return r.json();
    });
  }

  function setQuantity(key, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Returns a promise resolving to true if anything was corrected.
   * Lines are corrected one at a time: /cart/change.js returns the whole cart
   * and line keys stay stable, but serialising avoids racing the Ajax API.
   */
  function reconcile() {
    if (running) return Promise.resolve(false);
    running = true;

    return getCart()
      .then(function (cart) {
        var fixes = [];
        (cart.items || []).forEach(function (item) {
          var needed = shortfallQuantity(item);
          if (needed) fixes.push({ key: item.key, quantity: needed });
        });

        return fixes.reduce(function (chain, fix) {
          return chain.then(function () {
            return setQuantity(fix.key, fix.quantity);
          });
        }, Promise.resolve()).then(function () {
          return fixes.length > 0;
        });
      })
      .catch(function (err) {
        // Never block the customer on a guard failure -- fail open, matching
        // the Layer 2 "Allow all customers to submit checkout" setting.
        console.warn("[cart-minimum-guard] reconcile failed:", err);
        return false;
      })
      .then(function (changed) {
        running = false;
        return changed;
      });
  }

  // On the cart page, repair on load and re-render so totals are honest.
  function onLoad() {
    if (window.location.pathname.indexOf("/cart") !== 0) return;
    reconcile().then(function (changed) {
      if (changed) window.location.reload();
    });
  }

  // Last chance before handing off to checkout, from anywhere on the site
  // (cart page or drawer). Capture phase so it runs before the theme's own
  // submit handling.
  function onCheckoutClick(event) {
    var trigger = event.target && event.target.closest
      ? event.target.closest('[name="checkout"], .checkout__button, [data-checkout-button]')
      : null;
    if (!trigger || trigger.dataset.minimumGuardChecked === "1") return;

    event.preventDefault();
    event.stopPropagation();

    reconcile().then(function () {
      trigger.dataset.minimumGuardChecked = "1";
      trigger.click();
    });
  }

  document.addEventListener("click", onCheckoutClick, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onLoad);
  } else {
    onLoad();
  }
})();
